import {
  Accordion,
  AccordionSummary,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  AccordionDetails,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Box,
  Checkbox,
  FormHelperText
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useSearchParams } from 'react-router-dom';
import BadgeNumber from 'components/BadgeNumber';
import { SectionStyled } from 'containers/FormStep1Container';
import { Controller, ControllerRenderProps, FieldValues, useController, useFormContext } from 'react-hook-form';
import { useEffect, useState } from 'react';
import CircularIcon from 'components/CircularIcon';
import DoneIcon from '@mui/icons-material/Done';
import { AuthorisedSignatoriesTerms } from '@xendit/id-open-banking-ekyb-common/dist/enums/submission';
import BottomButton from 'components/BottomButton';

export type ManagementsType = {
  name: string;
  position: string;
  email_address: string;
  phone_number: string;
  identity_document_id: string;
  address: string;
  gender: string;
  identity_number: string;
};

export default function FormStep3Section1Container() {
  const [searchParams] = useSearchParams();
  const section = searchParams.get('section') || '1';
  const { control, watch, setValue, getValues } = useFormContext();
  const entityManagements = watch('entity.managements') as ManagementsType[];
  const individualOrCollectiveAuthorized = watch('surat_kuasa.terms') as string;
  const isIndividualAuthorized = individualOrCollectiveAuthorized === AuthorisedSignatoriesTerms.SINGLE;
  const isCollectiveAuthorized = individualOrCollectiveAuthorized === AuthorisedSignatoriesTerms.JOINT;
  const [isAccordionExpanded, setIsAccordionExpanded] = useState(false);
  const { fieldState, formState } = useController({ name: 'surat_kuasa.management_authoriser_identity_numbers' });

  useEffect(() => {
    setIsAccordionExpanded(section === '1');
  }, [section]);

  useEffect(() => {
    if (fieldState.error) {
      setIsAccordionExpanded(true);
    }
  }, [fieldState.error]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // if AuthorisedSignatoriesTerms is SINGLE, make management_authoriser_identity_numbers default value the first array
    if (
      isIndividualAuthorized &&
      (getValues('surat_kuasa.management_authoriser_identity_numbers').length > 1 ||
        getValues('surat_kuasa.management_authoriser_identity_numbers').length === 0)
    ) {
      setValue('surat_kuasa.management_authoriser_identity_numbers', [entityManagements[0].identity_number]);
    }
  }, [getValues, isIndividualAuthorized, setValue, entityManagements]);

  const handleRadioOrCheckbox = (
    target: string,
    field: ControllerRenderProps<FieldValues, 'surat_kuasa.management_authoriser_identity_numbers'>
  ) => {
    // for individual, set value directly
    if (isIndividualAuthorized) {
      return field.onChange([target]);
    }

    // for collective, remove target from array
    if (isCollectiveAuthorized && field.value?.includes(target)) {
      return field.onChange(field.value.filter((item: string) => item !== target));
    }

    // for collective, add target to array
    return field.onChange([...field.value, target]);
  };

  return (
    <Accordion
      expanded={isAccordionExpanded}
      onChange={() => ['2', '1'].includes(section) && setIsAccordionExpanded((prev) => !prev)}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
        {section <= '1' ? (
          <BadgeNumber>1</BadgeNumber>
        ) : (
          <CircularIcon>
            <DoneIcon />
          </CircularIcon>
        )}
        <Typography color="surface.700" variant="h6" sx={{ marginLeft: 1 }}>
          Pemberi Kuasa
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Controller
          name="surat_kuasa.terms"
          control={control}
          render={({ field }) => (
            <RadioGroup defaultValue="1" sx={{ mb: 3 }} {...field}>
              <FormControlLabel
                value={AuthorisedSignatoriesTerms.SINGLE}
                control={<Radio />}
                label="Individual yang dapat bertindak sendiri-sendiri sesuai dengan Surat Kuasa dan/atau Anggaran Dasar Perusahaan"
              />
              <FormControlLabel
                value={AuthorisedSignatoriesTerms.JOINT}
                control={<Radio />}
                disabled={entityManagements?.length === 1}
                label="2 individual atau lebih yang dapat bertindak baik bersama-sama sesuai dengan Surat Kuasa dan/atau Anggaran Dasar Perusahaan"
              />
            </RadioGroup>
          )}
        />
        <SectionStyled>
          <Typography color="surface.700" variant="h7" component="p" sx={{ mb: 1 }}>
            Pemohon / Pemberi Kuasa
          </Typography>
          <Typography color="surface.700" component="p" sx={{ mb: 4 }}>
            Dengan memilih detail kami di halaman ini, kami:
          </Typography>
          <RadioGroup defaultValue={0}>
            <List>
              <Controller
                control={control}
                name="surat_kuasa.management_authoriser_identity_numbers"
                rules={{
                  validate: (value) => (isCollectiveAuthorized && value.length < 2 ? 'Pilih minimal 2 orang' : true)
                }}
                render={({ field, fieldState }) => (
                  <>
                    {entityManagements?.map((management) => (
                      <ListItem
                        key={management.identity_number}
                        disablePadding
                        sx={{
                          bgcolor: 'white',
                          display: 'flex',
                          borderBottom: '1px solid #F2F2F2'
                        }}
                      >
                        <ListItemButton dense onClick={() => handleRadioOrCheckbox(management.identity_number, field)}>
                          <ListItemIcon sx={{ flex: 1, p: 1 }}>
                            {isIndividualAuthorized ? (
                              <Radio
                                checked={field.value?.includes(management.identity_number)}
                                value={management.identity_number}
                              />
                            ) : (
                              <Checkbox
                                checked={field.value?.includes(management.identity_number)}
                                value={management.identity_number}
                              />
                            )}
                            <Box maxWidth="400px" width={'100%'}>
                              <Typography sx={{ color: 'surface.300', fontSize: 12 }}>Nama Lengkap</Typography>
                              <Typography sx={{ color: 'surface.700', fontWeight: 600, fontSize: 14 }}>
                                {management.name}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography sx={{ color: 'surface.300', fontSize: 12 }}>Nomor Identitas</Typography>
                              <Typography sx={{ color: 'surface.700', fontWeight: 600, fontSize: 14 }}>
                                {management.identity_number}
                              </Typography>
                            </Box>
                          </ListItemIcon>
                        </ListItemButton>
                      </ListItem>
                    ))}
                    {isCollectiveAuthorized && (
                      <FormHelperText error={Boolean(fieldState.error?.message)}>
                        {fieldState.error?.message}
                      </FormHelperText>
                    )}
                  </>
                )}
              />
            </List>
          </RadioGroup>
        </SectionStyled>
      </AccordionDetails>
      {section === '1' && <BottomButton isButtonLoading={formState.isSubmitting} />}
    </Accordion>
  );
}
